import { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import { db, auth, secondaryAuth } from "./firebase";
import {
  collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot,
  query, orderBy, setDoc, getDocs, where, getDoc
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, signInAnonymously
} from "firebase/auth";
import { FMT, fmtDate, getNextStudentCode } from "./shared";
import { EMAILJS_CONFIG } from "./emailjs-config";
import { useSchool } from "./schoolContext";

// ─── EMAIL NOTIFICATIONS ───
// Best-effort: failures are logged to console but never break the caller.
// Only sends if the parent has an email address.
// Exported for testability.
export async function sendNotification(parent, subject, message) {
  try {
    if (!parent || !parent.email) return; // silent skip if no email
    if (!EMAILJS_CONFIG.serviceId || !EMAILJS_CONFIG.templateId || !EMAILJS_CONFIG.publicKey) return;
    await emailjs.send(EMAILJS_CONFIG.serviceId, EMAILJS_CONFIG.templateId, {
      to_name: parent.name || "Padre",
      to_email: parent.email,
      subject,
      message,
      total_amount: "",
      invoice_count: ""
    }, EMAILJS_CONFIG.publicKey);
  } catch (e) {
    console.warn("Notification failed:", e);
  }
}

// ─── AUDIT LOG ───
// Records money-related actions to schools/{schoolId}/auditLog collection.
// Best-effort: failures are logged to console but never break the caller.
// Records: actor info, action type, target collection/id, timestamp, before/after (for key fields).
// NOTE: schoolId is now REQUIRED. In multi-tenant mode, every audit entry
// belongs to a specific school. If schoolId is null/undefined, the call is skipped.
async function audit(schoolId, action, target, details = {}) {
  if (!schoolId) {
    console.warn("audit() called without schoolId — skipping:", action, target);
    return;
  }
  try {
    const user = auth.currentUser;
    let actorName = "system";
    let actorRole = "unknown";
    if (user) {
      // Look up actor in the school's vendors/ or parents/ subcollections
      try {
        const vDoc = await getDoc(doc(db, "schools", schoolId, "vendors", user.uid));
        if (vDoc.exists()) { actorName = vDoc.data().name; actorRole = vDoc.data().isAdmin ? "admin" : "vendor"; }
        else {
          const pDoc = await getDoc(doc(db, "schools", schoolId, "parents", user.uid));
          if (pDoc.exists()) { actorName = pDoc.data().name; actorRole = "parent"; }
          else if (user.isAnonymous) { actorRole = "student"; }
        }
      } catch {}
    }
    await addDoc(collection(db, "schools", schoolId, "auditLog"), {
      action, // e.g. "order.complete", "invoice.create"
      target, // e.g. "orders/abc123"
      actorUid: user?.uid || null,
      actorName,
      actorRole,
      timestamp: new Date().toISOString(),
      ...details
    });
  } catch (e) {
    console.warn("Audit log failed:", e);
  }
}


// ─── PRODUCTS ───
export function useFirebaseProducts() {
  const { schoolId, role } = useSchool();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Wait until schoolId is resolved
    if (!schoolId) { setProducts([]); setLoading(false); return; }
    let unsubSnap = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubSnap) { unsubSnap(); unsubSnap = null; }
      if (!user) { setProducts([]); setLoading(false); return; }
      // Only subscribe if the user belongs to this school (any role) or is anonymous
      // (anonymous = student logging in to browse menu). Otherwise rules will reject.
      if (!user.isAnonymous && !role) { setProducts([]); setLoading(false); return; }
      unsubSnap = onSnapshot(collection(db, "schools", schoolId, "products"), (snap) => {
        setProducts(snap.docs.map(d => ({ ...d.data(), id: d.id })));
        setLoading(false);
      }, (err) => { console.warn("products listener error:", err.code); setLoading(false); });
    });
    return () => { if (unsubSnap) unsubSnap(); unsubAuth(); };
  }, [schoolId, role]);
  const updateProduct = async (id, data) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "products", id), data);
  };
  const addProduct = async (product) => {
    if (!schoolId) throw new Error("No school context");
    return (await addDoc(collection(db, "schools", schoolId, "products"), product)).id;
  };
  return { products, loading, updateProduct, addProduct };
}

// ─── ORDERS ───
export function useFirebaseOrders() {
  const { schoolId, role } = useSchool();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!schoolId) { setOrders([]); setLoading(false); return; }
    let unsubSnap = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubSnap) { unsubSnap(); unsubSnap = null; }
      if (!user) { setOrders([]); setLoading(false); return; }
      // Only subscribe if the user can read the full orders list (vendor/admin/super_admin)
      // Parents and students don't list all orders here.
      if (role !== "vendor" && role !== "admin" && role !== "super_admin") {
        setOrders([]); setLoading(false); return;
      }
      const q = query(collection(db, "schools", schoolId, "orders"), orderBy("timestamp", "asc"));
      unsubSnap = onSnapshot(q,
        (snap) => { setOrders(snap.docs.map(d => ({ ...d.data(), id: d.id }))); setLoading(false); },
        (err) => { console.warn("orders listener error:", err.code); setLoading(false); }
      );
    });
    return () => { if (unsubSnap) unsubSnap(); unsubAuth(); };
  }, [schoolId, role]);
  const addOrder = async (order) => {
    if (!schoolId) throw new Error("No school context");
    const ref = await addDoc(collection(db, "schools", schoolId, "orders"), order);
    audit(schoolId, "order.create", `orders/${ref.id}`, { total: order.total, studentId: order.studentId || null, paymentMethod: order.paymentMethod });
  };

  const addScheduledOrders = async (ordersArr) => {
    if (!schoolId) throw new Error("No school context");
    for (const o of ordersArr) {
      const ref = await addDoc(collection(db, "schools", schoolId, "orders"), o);
      audit(schoolId, "order.create", `orders/${ref.id}`, { total: o.total, studentId: o.studentId || null, scheduled: true, scheduledDate: o.scheduledDate });
    }
  };
  const completeOrder = async (id, finalPaymentMethod = null) => {
    if (!schoolId) throw new Error("No school context");
    // First get the order to check if balance deduction is needed
    const orderSnap = await getDoc(doc(db, "schools", schoolId, "orders", id));
    const updateData = { status: "completed", completedAt: new Date().toISOString() };
    // If a final payment method is provided (e.g., "sinpe" for autonomous students), override it
    if (finalPaymentMethod) updateData.paymentMethod = finalPaymentMethod;
    await updateDoc(doc(db, "schools", schoolId, "orders", id), updateData);
    audit(schoolId, "order.complete", `orders/${id}`, finalPaymentMethod ? { finalPaymentMethod } : {});
    // If autonomous student paid with balance, deduct now (at completion time)
    if (orderSnap.exists()) {
      const o = orderSnap.data();
      if (o.autonomous && o.paymentMethod === "balance" && o.studentId) {
        try {
          const studentRef = doc(db, "schools", schoolId, "students", o.studentId);
          const studentSnap = await getDoc(studentRef);
          if (studentSnap.exists()) {
            const current = studentSnap.data().balance || 0;
            const newBalance = Math.max(0, current - o.total);
            await updateDoc(studentRef, { balance: newBalance });
            audit(schoolId, "student.balance_deduct", `students/${o.studentId}`, {
              studentName: o.studentName, amount: o.total,
              previousBalance: current, newBalance, orderId: id
            });
          }
        } catch (e) {
          console.error("Balance deduct on complete failed:", e.code, e.message);
        }
      }
    }
  };
  const requeueOrder = async (id) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "orders", id), { status: "pending", completedAt: null });
    audit(schoolId, "order.requeue", `orders/${id}`);
  };
  const updateOrderItems = async (id, items, total) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "orders", id), { items, total });
    audit(schoolId, "order.edit", `orders/${id}`, { newTotal: total });
  };
  const cancelOrder = async (id) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "orders", id), { status: "cancelled", cancelledAt: new Date().toISOString() });
    audit(schoolId, "order.cancel", `orders/${id}`);
  };
  const clearCompleted = async () => {
    if (!schoolId) throw new Error("No school context");
    const snap = await getDocs(collection(db, "schools", schoolId, "orders"));
    const completed = snap.docs.filter(d => d.data().status === "completed");
    await Promise.all(completed.map(d => deleteDoc(d.ref)));
    audit(schoolId, "order.clear_completed", "orders", { count: completed.length });
  };
  return { orders, loading, addOrder, addScheduledOrders, completeOrder, requeueOrder, cancelOrder, updateOrderItems, clearCompleted };
}

// ─── AUTH (Parents) ───
export function useParentAuth() {
  const { schoolId, refresh: refreshSchool } = useSchool();
  const [user, setUser] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) { setUser(null); setParentData(null); setLoading(false); return; }
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const snap = await getDoc(doc(db, "schools", schoolId, "parents", u.uid));
          if (snap.exists()) setParentData({ ...snap.data(), id: u.uid });
          else setParentData(null);
        } catch { setParentData(null); }
      } else { setParentData(null); }
      setLoading(false);
    });
    return unsub;
  }, [schoolId]);

  const register = async (email, password, name, phone) => {
    if (!schoolId) throw new Error("No school context");
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const data = { name, email, phone, createdAt: new Date().toISOString() };
    // Write userRoles FIRST so the SchoolContext can resolve the role for the new user.
    // This is also required for the rules to allow subsequent reads/writes by this parent.
    await setDoc(doc(db, "userRoles", cred.user.uid), {
      role: "parent",
      schoolId,
      email,
      createdAt: new Date().toISOString()
    });
    // Then write the parent profile in the school's subcollection
    await setDoc(doc(db, "schools", schoolId, "parents", cred.user.uid), data);
    setParentData({ ...data, id: cred.user.uid });
    // Trigger SchoolContext to re-read userRoles so role becomes "parent"
    // and downstream listeners (useStudents, useParentInvoices, orders) start working.
    refreshSchool();
    return cred.user;
  };

  const login = async (email, password) => {
    if (!schoolId) throw new Error("No school context");
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const snap = await getDoc(doc(db, "schools", schoolId, "parents", cred.user.uid));
    if (snap.exists()) setParentData({ ...snap.data(), id: cred.user.uid });
    else throw new Error("Este padre no está registrado en esta escuela");
    return cred.user;
  };

  const logout = async () => { await signOut(auth); setUser(null); setParentData(null); };

  return { user, parentData, loading, register, login, logout };
}

// ─── STUDENTS (per parent) ───
export function useStudents(parentId) {
  const { schoolId, role } = useSchool();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId || !parentId) { setStudents([]); setLoading(false); return; }
    // Wait until role is resolved — otherwise the listener subscribes before
    // userRoles/{uid} is visible to Firestore rules and gets permission-denied.
    if (!role) { setStudents([]); setLoading(false); return; }
    const q = query(collection(db, "schools", schoolId, "students"), where("parentId", "==", parentId));
    const unsub = onSnapshot(q,
      (snap) => {
        setStudents(snap.docs.map(d => ({ ...d.data(), id: d.id })));
        setLoading(false);
      },
      (err) => { console.warn("students(parent) listener error:", err.code); setLoading(false); }
    );
    return unsub;
  }, [schoolId, parentId, role]);

  const addStudent = async (data) => {
    if (!schoolId) throw new Error("No school context");
    const ref = await addDoc(collection(db, "schools", schoolId, "students"), { ...data, parentId, active: true, createdAt: new Date().toISOString() });
    return ref.id;
  };

  const updateStudent = async (id, data) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "students", id), data);
  };

  return { students, loading, addStudent, updateStudent };
}

// ─── Autonomous students hook (used by vendors/admins) ───
// Loads ALL autonomous students for the school (no parentId filter)
export function useAutonomousStudents() {
  const { schoolId, role } = useSchool();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId || !role) { setStudents([]); setLoading(false); return; }
    const q = query(
      collection(db, "schools", schoolId, "students"),
      where("autonomous", "==", true)
    );
    const unsub = onSnapshot(q, snap => {
      setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [schoolId, role]);

  const addAutonomousStudent = async (data) => {
    if (!schoolId) throw new Error("No school context");
    const code = data.code || await getNextStudentCode(db, schoolId);
    const ref = await addDoc(collection(db, "schools", schoolId, "students"), {
      ...data,
      code,
      autonomous: true,
      parentId: null,
      balance: 0,
      active: true,
      createdAt: new Date().toISOString(),
    });
    audit(schoolId, "student.create_autonomous", `students/${ref.id}`, { name: data.name });
    return { id: ref.id, code };
  };

  const updateAutonomousStudent = async (id, data) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "students", id), data);
  };

  const rechargeBalance = async (studentId, amount, studentName) => {
    if (!schoolId) throw new Error("No school context");
    const ref = doc(db, "schools", schoolId, "students", studentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Estudiante no encontrado");
    const current = snap.data().balance || 0;
    const newBalance = current + amount;
    await updateDoc(ref, { balance: newBalance });
    audit(schoolId, "student.balance_recharge", `students/${studentId}`, {
      studentName, amount, previousBalance: current, newBalance
    });
    return newBalance;
  };

  const deductBalance = async (studentId, amount, studentName, orderId) => {
    if (!schoolId) throw new Error("No school context");
    const ref = doc(db, "schools", schoolId, "students", studentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Estudiante no encontrado");
    const current = snap.data().balance || 0;
    if (current < amount) throw new Error(`Saldo insuficiente (disponible: ₡${current})`);
    const newBalance = current - amount;
    await updateDoc(ref, { balance: newBalance });
    audit(schoolId, "student.balance_deduct", `students/${studentId}`, {
      studentName, amount, previousBalance: current, newBalance, orderId
    });
    return newBalance;
  };

  return { students, loading, addAutonomousStudent, updateAutonomousStudent, rechargeBalance, deductBalance };
}

// ─── STUDENT LOGIN ───
// Students don't have their own Firebase Auth accounts. We use anonymous auth
// so they can read the students collection (allowed by rules for any auth user
// in this app) and validate their code+accessCode.
// ─── STUDENT LOGIN ───
// Students don't have their own Firebase Auth accounts. We use anonymous auth
// so they can read the students subcollection and validate their code+accessCode.
// Requires schoolId explicitly since students don't have a userRoles document.
export async function loginStudent(schoolId, code, accessCode) {
  if (!schoolId) throw new Error("Falta el contexto de la escuela");
  // Make sure we have an auth session (anonymous if no user is signed in)
  if (!auth.currentUser) {
    await signInAnonymously(auth);
  }
  const q = query(collection(db, "schools", schoolId, "students"), where("code", "==", code.toUpperCase()));
  const snap = await getDocs(q);
  if (snap.empty) throw new Error("Código de estudiante no encontrado");
  const student = { ...snap.docs[0].data(), id: snap.docs[0].id };
  if (student.active === false) throw new Error("Esta cuenta de estudiante está desactivada");
  if (student.accessCode !== accessCode) throw new Error("Código de acceso incorrecto");
  return student;
}

// ─── INVOICES (for Parents) ───
export function useParentInvoices(parentId) {
  const { schoolId, role } = useSchool();
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    if (!schoolId || !parentId) { setInvoices([]); return; }
    // Wait for role so Firestore rules can evaluate isParentOf correctly
    if (!role) { setInvoices([]); return; }
    const q = query(collection(db, "schools", schoolId, "invoices"), where("parentId", "==", parentId), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q,
      (snap) => { setInvoices(snap.docs.map(d => ({ ...d.data(), id: d.id }))); },
      (err) => { console.warn("parentInvoices listener error:", err.code); }
    );
    return unsub;
  }, [schoolId, parentId, role]);

  const submitPayment = async (invoiceId, imageUrl) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "invoices", invoiceId), {
      status: "pago_enviado", paymentImage: imageUrl, paymentDate: new Date().toISOString()
    });
    audit(schoolId, "invoice.payment_submitted", `invoices/${invoiceId}`);
  };

  return { invoices, submitPayment };
}

// ─── INVOICES (for Vendor) ───
export function useVendorInvoices() {
  const { schoolId, role } = useSchool();
  const [invoices, setInvoices] = useState([]);
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) { setInvoices([]); setParents([]); setStudents([]); setLoading(false); return; }
    // Only vendor/admin/super_admin can subscribe — others would get permission-denied
    if (role !== "vendor" && role !== "admin" && role !== "super_admin") {
      setInvoices([]); setParents([]); setStudents([]); setLoading(false); return;
    }
    let subs = [];
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      subs.forEach(u => u()); subs = [];
      if (!user) { setInvoices([]); setParents([]); setStudents([]); setLoading(false); return; }
      const u1 = onSnapshot(query(collection(db, "schools", schoolId, "invoices"), orderBy("createdAt", "desc")),
        (snap) => { setInvoices(snap.docs.map(d => ({ ...d.data(), id: d.id }))); setLoading(false); },
        (err) => { console.warn("invoices listener error:", err.code); setLoading(false); }
      );
      const u2 = onSnapshot(collection(db, "schools", schoolId, "parents"),
        (snap) => { setParents(snap.docs.map(d => ({ ...d.data(), id: d.id }))); },
        (err) => { console.warn("parents listener error:", err.code); }
      );
      const u3 = onSnapshot(collection(db, "schools", schoolId, "students"),
        (snap) => { setStudents(snap.docs.map(d => ({ ...d.data(), id: d.id }))); },
        (err) => { console.warn("students listener error:", err.code); }
      );
      subs = [u1, u2, u3];
    });
    return () => { subs.forEach(u => u()); unsubAuth(); };
  }, [schoolId, role]);

  const createInvoice = async (invoiceData) => {
    if (!schoolId) throw new Error("No school context");
    const ref = await addDoc(collection(db, "schools", schoolId, "invoices"), {
      ...invoiceData, status: "pendiente", createdAt: new Date().toISOString()
    });
    audit(schoolId, "invoice.create", `invoices/${ref.id}`, { parentId: invoiceData.parentId, total: invoiceData.total });
    // Best-effort notification (Notif A: factura nueva)
    const parent = parents.find(p => p.id === invoiceData.parentId);
    if (parent?.email) {
      const itemsTxt = (invoiceData.studentDetails || [])
        .map(sd => `  • ${sd.studentName} (${sd.studentCode}): ${FMT(sd.total)}`).join("\n");
      const msg = `Hola ${parent.name},\n\nTiene una nueva factura pendiente en La Tiendita CR.\n\n📅 Semana: ${fmtDate(invoiceData.weekStart)} — ${fmtDate(invoiceData.weekEnd)}\n💰 Total: ${FMT(invoiceData.total)}\n\nDetalle por estudiante:\n${itemsTxt}\n\nPuede subir el comprobante de pago desde el Portal de Padres.\n\nGracias,\nLa Tiendita CR`;
      sendNotification(parent, `Nueva factura - ${FMT(invoiceData.total)}`, msg);
    }
  };

  const approvePayment = async (invoiceId) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "invoices", invoiceId), {
      status: "pagada", approvedAt: new Date().toISOString()
    });
    audit(schoolId, "invoice.approve_payment", `invoices/${invoiceId}`);
    // Best-effort notification (Notif C: pago aprobado)
    const inv = invoices.find(i => i.id === invoiceId);
    if (inv) {
      const parent = parents.find(p => p.id === inv.parentId);
      if (parent?.email) {
        const msg = `Hola ${parent.name},\n\nSu pago ha sido recibido y aprobado.\n\n📅 Semana: ${fmtDate(inv.weekStart)} — ${fmtDate(inv.weekEnd)}\n💰 Total pagado: ${FMT(inv.total)}\n\n¡Gracias por su pago!\n\nLa Tiendita CR`;
        sendNotification(parent, `Pago aprobado - ${FMT(inv.total)}`, msg);
      }
    }
  };

  const cancelInvoice = async (invoiceId) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "invoices", invoiceId), {
      status: "cancelada", cancelledAt: new Date().toISOString()
    });
    audit(schoolId, "invoice.cancel", `invoices/${invoiceId}`);
  };

  // Mark invoice as paid without a payment image (e.g. parent paid via WhatsApp/SINPE
  // and the admin confirmed verbally or via chat). Sets paidManually: true so the UI
  // can distinguish this from a comprobante-backed payment.
  const markPaidManually = async (invoiceId) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "invoices", invoiceId), {
      status: "pagada",
      paidManually: true,
      approvedAt: new Date().toISOString(),
    });
    audit(schoolId, "invoice.mark_paid_manually", `invoices/${invoiceId}`);
    const inv = invoices.find(i => i.id === invoiceId);
    if (inv) {
      const parent = parents.find(p => p.id === inv.parentId);
      if (parent?.email) {
        const msg = `Hola ${parent.name},\n\nSu pago ha sido registrado y aprobado.\n\n📅 Semana: ${fmtDate(inv.weekStart)} — ${fmtDate(inv.weekEnd)}\n💰 Total: ${FMT(inv.total)}\n\n¡Gracias!\n\nLa Tiendita CR`;
        sendNotification(parent, `Pago aprobado - ${FMT(inv.total)}`, msg);
      }
    }
  };

  return { invoices, parents, students, loading, createInvoice, approvePayment, cancelInvoice, markPaidManually };
}

// ─── VENDORS (sellers) ───
// Vendors are Firebase Auth accounts. Document at vendors/{uid} stores profile.
// Document at userRoles/{uid} stores role for security rules to check.
// "General" vendor is a placeholder doc with no auth account — used only to label scheduled orders.
export function useVendors() {
  const { schoolId, role } = useSchool();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) { setVendors([]); setLoading(false); return; }
    let unsubSnap = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubSnap) { unsubSnap(); unsubSnap = null; }
      if (!user) { setVendors([]); setLoading(false); return; }
      // Anyone belonging to the school can read vendors (rules allow belongsToSchool).
      // But a user with no role at all (no userRoles doc + not anonymous) can't.
      if (!user.isAnonymous && !role) { setVendors([]); setLoading(false); return; }
      unsubSnap = onSnapshot(collection(db, "schools", schoolId, "vendors"),
        (snap) => {
          const existing = snap.docs.map(d => d.id);
          if (!existing.includes("vendor_general") && (role === "admin" || role === "super_admin")) {
            // Only admin can create the seed doc
            setDoc(doc(db, "schools", schoolId, "vendors", "vendor_general"), {
              name: "General", email: null, active: true, isDefault: true, isAdmin: false, locations: ["loc_general"]
            }).catch(() => {});
          }
          setVendors(snap.docs.map(d => ({ ...d.data(), id: d.id })));
          setLoading(false);
        },
        (err) => { console.warn("vendors listener error:", err.code); setLoading(false); }
      );
    });
    return () => { if (unsubSnap) unsubSnap(); unsubAuth(); };
  }, [schoolId, role]);

  // Create a vendor: creates Auth user, schools/{schoolId}/vendors/{uid} doc,
  // and userRoles/{uid} doc with schoolId.
  // Uses secondary auth instance so the current admin session is NOT replaced.
  const addVendor = async (data) => {
    if (!schoolId) throw new Error("No school context");
    if (!data.email || !data.password) throw new Error("Email y contraseña requeridos");
    if (data.password.length < 6) throw new Error("La contraseña debe tener al menos 6 caracteres");
    const cred = await createUserWithEmailAndPassword(secondaryAuth, data.email.trim(), data.password);
    const uid = cred.user.uid;
    await setDoc(doc(db, "schools", schoolId, "vendors", uid), {
      name: data.name.trim(),
      email: data.email.trim(),
      active: true,
      isDefault: false,
      isAdmin: !!data.isAdmin,
      locations: data.locations || []
    });
    await setDoc(doc(db, "userRoles", uid), {
      role: data.isAdmin ? "admin" : "vendor",
      schoolId,
      email: data.email.trim(),
      createdAt: new Date().toISOString()
    });
    await signOut(secondaryAuth);
    return uid;
  };

  const updateVendor = async (id, data) => {
    if (!schoolId) throw new Error("No school context");
    if (id === "vendor_general") throw new Error("No se puede modificar el vendedor General");
    await updateDoc(doc(db, "schools", schoolId, "vendors", id), data);
    // Keep userRoles in sync if isAdmin changed
    if (data.isAdmin !== undefined) {
      await setDoc(doc(db, "userRoles", id), { role: data.isAdmin ? "admin" : "vendor", schoolId }, { merge: true });
    }
  };

  return { vendors, loading, addVendor, updateVendor };
}

// ─── LOCATIONS ───
export function useLocations() {
  const { schoolId, role } = useSchool();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) { setLocations([]); setLoading(false); return; }
    let unsubSnap = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubSnap) { unsubSnap(); unsubSnap = null; }
      if (!user) { setLocations([]); setLoading(false); return; }
      if (!user.isAnonymous && !role) { setLocations([]); setLoading(false); return; }
      unsubSnap = onSnapshot(collection(db, "schools", schoolId, "locations"),
        (snap) => {
          if (snap.empty && (role === "admin" || role === "super_admin")) {
            setDoc(doc(db, "schools", schoolId, "locations", "loc_general"), { name: "General", active: true, isDefault: true }).catch(() => {});
            return;
          }
          setLocations(snap.docs.map(d => ({ ...d.data(), id: d.id })));
          setLoading(false);
        },
        (err) => { console.warn("locations listener error:", err.code); setLoading(false); }
      );
    });
    return () => { if (unsubSnap) unsubSnap(); unsubAuth(); };
  }, [schoolId, role]);

  const addLocation = async (data) => {
    if (!schoolId) throw new Error("No school context");
    return (await addDoc(collection(db, "schools", schoolId, "locations"), { ...data, isDefault: false })).id;
  };
  const updateLocation = async (id, data) => {
    if (!schoolId) throw new Error("No school context");
    await updateDoc(doc(db, "schools", schoolId, "locations", id), data);
  };
  return { locations, loading, addLocation, updateLocation };
}

// ─── VENDOR LOGIN ───
// Logs in via Firebase Auth using email + password, then loads vendor profile.
// After auth, looks up userRoles/{uid} to find the schoolId, then reads
// schools/{schoolId}/vendors/{uid} for the vendor profile.
// If expectedSchoolId is provided (from the URL slug context), the vendor's
// schoolId must match — otherwise the login is rejected to prevent users
// from logging into a school they don't belong to.
export async function loginVendor(email, password, expectedSchoolId = null) {
  let cred;
  try {
    cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  } catch (e) {
    if (e.code === "auth/invalid-credential" || e.code === "auth/wrong-password" || e.code === "auth/user-not-found") {
      throw new Error("Correo o contraseña incorrectos");
    }
    throw new Error(e.message);
  }
  const uid = cred.user.uid;
  // Resolve schoolId from userRoles
  const roleDoc = await getDoc(doc(db, "userRoles", uid));
  if (!roleDoc.exists()) {
    await signOut(auth);
    throw new Error("Esta cuenta no tiene rol asignado");
  }
  const roleData = roleDoc.data();
  if (roleData.role !== "vendor" && roleData.role !== "admin") {
    await signOut(auth);
    throw new Error("Esta cuenta no es de vendedor");
  }
  const schoolId = roleData.schoolId;
  if (!schoolId) {
    await signOut(auth);
    throw new Error("Esta cuenta no tiene escuela asignada");
  }
  // Enforce school match: if we expected a specific school (from URL), reject if different
  if (expectedSchoolId && schoolId !== expectedSchoolId) {
    await signOut(auth);
    throw new Error("Esta cuenta pertenece a otra escuela");
  }
  // Load vendor profile from the school's subcollection
  const vDoc = await getDoc(doc(db, "schools", schoolId, "vendors", uid));
  if (!vDoc.exists()) {
    await signOut(auth);
    throw new Error("Perfil de vendedor no encontrado en esta escuela");
  }
  const vendor = { ...vDoc.data(), id: uid };
  if (!vendor.active) {
    await signOut(auth);
    throw new Error("Vendedor inactivo");
  }
  return vendor;
}

// Vendor logout
export async function logoutVendor() {
  try { await signOut(auth); } catch (e) {}
}

// ─── VENDOR SESSION ───
// Hook to track currently logged-in vendor (persists across page refresh via Firebase Auth).
// After auth, resolves the vendor's schoolId from userRoles/{uid} and loads
// their profile from schools/{schoolId}/vendors/{uid}. If the current URL has
// a different school slug than the vendor's school, rejects the session.
export function useVendorSession() {
  const { schoolId: contextSchoolId } = useSchool();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setVendor(null); setLoading(false); return; }
      try {
        // Resolve schoolId from userRoles
        const roleDoc = await getDoc(doc(db, "userRoles", user.uid));
        if (!roleDoc.exists()) { setVendor(null); setLoading(false); return; }
        const roleData = roleDoc.data();
        if (roleData.role !== "vendor" && roleData.role !== "admin") {
          setVendor(null); setLoading(false); return;
        }
        const sid = roleData.schoolId;
        if (!sid) { setVendor(null); setLoading(false); return; }
        // Cross-check with URL slug context
        if (contextSchoolId && sid !== contextSchoolId) {
          setVendor(null); setLoading(false); return;
        }
        // Load vendor profile from the school's subcollection
        const vDoc = await getDoc(doc(db, "schools", sid, "vendors", user.uid));
        if (vDoc.exists() && vDoc.data().active) {
          setVendor({ ...vDoc.data(), id: user.uid });
        } else {
          setVendor(null);
        }
      } catch (e) {
        setVendor(null);
      }
      setLoading(false);
    });
    return unsub;
  }, [contextSchoolId]);
  return { vendor, loading };
}
